import { useEffect, useRef } from "react";
import { useDisclosure } from "@mantine/hooks";
import { Notifications } from "@mantine/notifications";
import {
  Button,
  AppShell,
  Badge,
  Burger,
  Group,
  createTheme,
  MantineProvider,
  NumberFormatter,
  Text,
  rem,
} from "@mantine/core";
import { Link, useLocation } from "wouter";
import mixpanel from "mixpanel-browser";
import NavBar from "./NavBar.jsx";
import { useStore } from "./store";
import { getMyProfile } from "./api.js";
import PlanBadge from "./Profile/PlanBadge.jsx";
import { IconCoins } from "@tabler/icons-react";
import PhotoVortexIcon from "./PhotoVortexIcon.jsx";
import AppRoutes from "./AppRoutes.jsx";
import * as Sentry from "@sentry/browser";
import { ErrorBoundary } from "@sentry/react";
import Fallback from "./_Common/Fallback.jsx";

function App() {
  const [opened, { toggle }] = useDisclosure();
  const [location, setLocation] = useLocation();
  const authToken = useStore((state) => state.authToken);
  const profile = useStore((state) => state.profile);
  const setProfile = useStore((state) => state.setProfile);
  const initialized = useRef(false);

  useEffect(() => {
    if (authToken) {
      if (!initialized.current) {
        initialized.current = true;
        getMyProfile().then((data) => {
          setProfile(data);
          mixpanel.identify(data.userId);
          Sentry.setUser({ userId: data.userId });
          // Redirect free users to payment immediately:
          if (data.plan === "Free" && !location.startsWith("/payment")) {
            setLocation("/payment/plans");
          }
        });
      }
    }
  }, [authToken, profile]);

  const myColor = [
    "#e1f9ff",
    "#ccedff",
    "#9ad7ff",
    "#64c1ff",
    "#3baefe",
    "#20a2fe",
    "#099cff",
    "#0088e4",
    "#0078cd",
    "#0069b6",
  ];

  const theme = createTheme({
    components: {
      Button: Button.extend({
        styles: {
          inner: { fontWeight: 100 },
        },
      }),
    },
    fontFamily: "Helvetica, sans-serif",
    fontFamilyMonospace: "Courier, monospace",
    headings: { fontFamily: "Helvetica, sans-serif", fontWeight: "100" },
    white: "#fafafe",
    black: "#08080a",
    fontSizes: {
      xs: rem(14),
      sm: rem(16),
      md: rem(18),
      lg: rem(20),
      xl: rem(24),
    },
    primaryColor: "myColor",
    colors: {
      myColor,
    },
  });

  const icon = (
    <IconCoins stroke={1.5} style={{ width: rem(14), height: rem(14) }} />
  );

  // const jobs = useStore((state) => state.jobs);
  // const waitingQueue = useStore((state) => state.waitingQueue);

  return (
    <MantineProvider theme={theme} defaultColorScheme="dark">
      <Notifications />
      <AppShell
        header={{ height: 50 }}
        navbar={{
          width: 160,
          breakpoint: "sm",
          collapsed: { mobile: !opened },
        }}
        padding="sm"
      >
        <AppShell.Header>
          <Group
            wrap="nowrap"
            justify="space-between"
            h="100%"
            px="md"
            style={{ overflow: "hidden" }}
          >
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
            />
            <PhotoVortexIcon width="32" color="#152232" />
            <Text visibleFrom="sm">PhotoVortex</Text>
            {/* <Badge color="gray" size="sm">
              Beta
            </Badge> */}
            <div style={{ flexGrow: 1 }}>
              {/* <Group visibleFrom="md" justify="center">
                <Badge variant="dot" color="red">
                  {waitingQueue.length} queued
                </Badge>
                <Badge variant="dot" color="blue">
                  {jobs.length} processing
                </Badge>
              </Group> */}
            </div>
            {profile && authToken ? (
              <>
                <PlanBadge plan={profile.plan} />
                <Badge
                  color={profile.credits <= 0 ? "red" : "myColor"}
                  variant={profile.credits <= 0 ? "filled" : "light"}
                  component={Link}
                  leftSection={icon}
                  to="/profile"
                  style={{ cursor: "pointer" }}
                >
                  <NumberFormatter
                    value={profile.credits}
                    decimalScale={0}
                    thousandSeparator
                  />
                </Badge>
              </>
            ) : (
              <Button component={Link} to="/auth">
                Login
              </Button>
            )}
          </Group>
        </AppShell.Header>

        <AppShell.Navbar p={0}>
          <NavBar toggle={toggle} />
        </AppShell.Navbar>

        <AppShell.Main padding={0}>
          <ErrorBoundary fallback={Fallback}>
            <AppRoutes />
          </ErrorBoundary>
        </AppShell.Main>
      </AppShell>
    </MantineProvider>
  );
}

export default App;
